import { useEffect, useMemo, useState } from 'react';
import { Clock, CalendarDays, Plane, Check, LogOut } from '../../../icons';
import { Modal, ModalActions, ModalFootNote, Avatar } from '../../../components/shared';
// 근태 5단계(2026-08-24) — 신청이 곧 기안이다. 서버가 조직에서 결재선을 뽑아 문서를 만든다.
import { createLeaveWithApproval, createBusinessTripWithApproval, type LeaveType, type TripType, type ApprovalSubmitDto } from '../../../api/hr';
import { leaveAvailable } from '../../../lib/leave-balance';
import { extractErrorMessage } from '../../../api/approval';
import { invalidateWidgetCache } from '../../../lib/widget-cache';
import { useWorkStatusStore } from '../../../store/workStatusStore';
import { isCheckOutLocked } from '../../../lib/work-status';
import { useClockTick, formatClock } from '../../../hooks/useClockTick';
import { getDateLocale } from '../../../lib/base-i18n';
import { useAuthStore } from '../../../store/authStore';
import type { AttendanceTab } from '../board/widget-context';
import { useLeaveSummary } from '../board/widget-queries';
// 기준 설정 (2026-08-28 · 판 7) — 종류의 «이름» 과 «씀 여부» 는 회사가 정한다.
//   못 읽으면 아래 기본 목록이 그대로 산다(깜빡이지 않는다).
import { useCompanyCodes } from '../../../lib/company-codes';
// 근태 B단계(2026-08-24) — 휴가 길이는 분이 진실이고 1일은 그 사람의 소정근로시간이다.
import { formatLeaveAmount, leaveTotalText, DEFAULT_DAILY_WORK_MINUTES } from '../../../lib/leave-amount';
// 결재 A6(2026-08-24) — 결재선 블록은 **공통 조각 하나**다. 기안 화면과 같은 것을 쓴다:
// *"위 칸(서식)만 종류마다 다르고 아래는 똑같다."*
import { ApprovalLineBlock, EMPTY_LINE, type ApprovalLineValue } from '../../ApprovalPage/ApprovalLineBlock';
// 대시보드 시안 v2 V8 (2026-09-15) — 휴가 유형 선택창 · 잔여 한 줄 · 잔여 없는 유형 막기(Q-11).
//   깃발이 꺼져 있으면(기본) 아래 알약 · 잔여 링이 종전 그대로 그려진다.
import { DASHBOARD_V2_ENABLED } from '../../../lib/layout-shell';
import { LeaveTypeField } from './LeaveTypeField';
import { buildLeaveTypeGroups, isLeaveTypeBlocked } from './leave-type-groups';
// 2026-09-16 — 휴가 종류 목록은 `lib/leave-type.ts` 한 곳에서 온다(사본 합치기 · v2 V12 사전 정리).
//   종전에는 이 파일이 여덟을 직접 들고 있어, 위젯 · api · lib 의 사본과 따로 놀 수 있었다.
//   S6 의 합친 코드(`half` · `quarter`)도 그 목록에 함께 있다.
import { LEAVE_TYPES, isPartialDayType, leaveTypeLabel } from '../../../lib/leave-type';
// S6 (2026-09-16) — 반차 · 반반차 «시작 시각». 새 서버(근무제 응답에 `partialLeave`)에서만 오전/오후가 합쳐지고 시각을 고른다.
import axios from 'axios';
import { TimePicker } from '../../../components/shared';
import { getWorkPolicy, type WorkPolicyDto } from '../../../api/hr-schedule';
import {
  checkStart, durationText, endTimeFor, isLegacyPartialType, isLegacyServer, isPartialLeaveType,
  leaveTimeErrorText, partialLeaveMinutes, startWindow, unavailableText, type LeaveTimeErrorBody,
} from '../../../lib/partial-leave-time';
import './partial-leave-time.css';

/**
 * 근태 신청 모달 — all-frame.html #2c.
 *
 * 출근·퇴근 / 휴가 / 출장 세 갈래를 한 모달에서 처리한다.
 *   · 휴가·출장은 기존 `api/hr` 엔드포인트를 그대로 쓴다(신규 API 0).
 *   · 출근·퇴근은 **서버 근태**(`/hr/attendance/*`)에 남긴다 — 2026-08-23 이관.
 *     상태바·대시보드 위젯과 같은 스토어를 쓰므로 어디서 찍어도 같은 값이 보인다.
 *
 * 신청에 성공하면 관련 캐시를 버려 휴가·근태 위젯이 곧바로 새 값을 집는다.
 */

/** 하루 안에서 끝나는 종류(반차·반반차) — 종료일 칸을 잠그고 시작일과 같게 보낸다. */
function isPartialDay(type: LeaveType): boolean {
  return isPartialDayType(type);
}

const TRIP_TYPES: Array<{ key: TripType; label: string }> = [
  { key: 'business_trip', label: '출장' },
  { key: 'outside_work', label: '외근' },
];

const TABS: Array<{ key: AttendanceTab; label: string; icon: typeof Clock }> = [
  { key: 'punch', label: '출근 · 퇴근', icon: Clock },
  { key: 'leave', label: '휴가', icon: CalendarDays },
  { key: 'trip', label: '출장', icon: Plane },
];

function todayIso(offsetDays = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** 달력일 기준 포함 일수(양 끝 포함). 출장 기간에도 같은 셈을 쓴다. */
function countDays(start: string, end: string): number {
  const a = new Date(start).getTime();
  const b = new Date(end).getTime();
  if (Number.isNaN(a) || Number.isNaN(b) || b < a) return 0;
  return Math.floor((b - a) / 86_400_000) + 1;
}

/** 신청 길이(분) — 서버 `calculateLeaveMinutes` 와 같은 규칙. */
function countMinutes(type: LeaveType, start: string, end: string, dailyMinutes: number): number {
  const fraction = LEAVE_TYPES.find((t) => t.key === type)?.fraction;
  if (fraction != null) return Math.round(dailyMinutes * fraction);
  return countDays(start, end) * dailyMinutes;
}

export function AttendanceRequestModal({ initialTab = 'leave', onClose }: { initialTab?: AttendanceTab; onClose: () => void }) {
  const user = useAuthStore((s) => s.user);
  const [tab, setTab] = useState<AttendanceTab>(initialTab);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * 신청이 결재로 올라갔는가 (2026-08-24).
   *
   * ★ 예전에는 신청이 저장만 되고 **영원히 PENDING** 이었다 — 승인할 길이 없었기 때문이다.
   *   이제 결재를 타므로, 올라갔는지 / 결재자를 못 찾았는지를 화면이 말해 준다.
   */
  const [approval, setApproval] = useState<ApprovalSubmitDto | null>(null);

  // 휴가 폼
  const [leaveType, setLeaveType] = useState<LeaveType>('annual');
  const [startDate, setStartDate] = useState(() => todayIso());
  const [endDate, setEndDate] = useState(() => todayIso());
  const [reason, setReason] = useState('');
  /**
   * S6 — 반차 · 반반차 시작 시각과 그날 근무제.
   * ★ 근무제는 **고른 날짜마다** 다시 받는다(교대 · 선택근로는 날마다 다르다). 받는 동안은 `undefined`.
   * ★ 새 서버인지는 한 번 알면 붙들어 둔다 — 날짜를 바꿔 다시 받는 사이 목록이 옛 오전/오후로 깜빡이지 않게.
   */
  const [startTime, setStartTime] = useState<string | null>(null);
  const [policy, setPolicy] = useState<WorkPolicyDto | null | undefined>(undefined);
  const [newServer, setNewServer] = useState(false);
  const [timeError, setTimeError] = useState<string | null>(null);

  /**
   * 결재선 (결재 A6, 2026-08-24) — 종전에는 **보지도 못했다.** 신청하면 서버가 조직에서
   * 자동으로 뽑아 문서를 만들고, 그 결과만 받았다. 이제 미리 보고 고친다.
   */
  const [line, setLine] = useState<ApprovalLineValue>(EMPTY_LINE);
  /**
   * v2 V9 (규칙 D5) — 출장 탭의 결재선은 **따로** 둔다. 종전에는 휴가 · 출장이 `line` 하나를 같이 써서
   * 휴가 결재선이 먼저 채워지면 출장의 회사 기본 결재선이 들어오지 않았다(비어 있을 때만 채우므로).
   * ★ 깃발이 꺼져 있으면 쓰지 않는다 — 종전 그대로 `line` 하나다.
   */
  const [tripLine, setTripLine] = useState<ApprovalLineValue>(EMPTY_LINE);

  // 출장 폼
  const [tripType, setTripType] = useState<TripType>('business_trip');
  const [destination, setDestination] = useState('');
  const [purpose, setPurpose] = useState('');

  // 출퇴근 — 서버 기록(상태바와 같은 스토어).
  const tick = useClockTick();
  const today = useWorkStatusStore((s) => s.today);
  const hydrated = useWorkStatusStore((s) => s.hydrated);
  // 오늘이 간주 출근일(승인된 출장·외근)이면 퇴근이 열린다 — 근태 D2, 2026-08-24.
  const deemed = useWorkStatusStore((s) => s.deemed);
  const punchPending = useWorkStatusStore((s) => s.pending);
  const punchError = useWorkStatusStore((s) => s.error);
  const hydrate = useWorkStatusStore((s) => s.hydrate);
  const selectStatus = useWorkStatusStore((s) => s.select);
  const rec = { checkIn: today.checkIn, checkOut: today.checkOut };
  const offLocked = isCheckOutLocked({ hydrated, checkIn: today.checkIn, deemed: Boolean(deemed) });
  // 대시보드를 안 거치고 모달만 연 경우를 위해 — 이미 읽어 뒀으면 다시 묻지 않는다.
  useEffect(() => { if (!hydrated) void hydrate(); }, [hydrated, hydrate]);

  const summary = useLeaveSummary();
  // 소정근로는 서버가 준다(근무제 사다리). 옛 서버는 안 주므로 그때는 8시간으로 본다.
  const dailyMinutes = summary.data?.dailyMinutes ?? DEFAULT_DAILY_WORK_MINUTES;
  /**
   * 반반차를 보여도 되는가 — **배포 순서 결합을 없애는 가드**.
   *
   * 반반차는 서버 enum(`leaveCreateBodySchema`)에 값이 늘어야 통과한다. 프론트만 먼저
   * 올라가면 고를 수는 있는데 신청이 400 이 된다 — 고를 수 있는데 안 되는 것이 제일 나쁘다.
   * `dailyMinutes` 는 같은 판에 생긴 칸이라 **서버가 새것인지의 신호**로 쓴다.
   * 아직 못 읽었을 때는 보여 준다(새 서버에서 깜빡이지 않게) — 옛 서버로 확인되면 그때 감춘다.
   */
  const supportsQuarter = summary.data == null || summary.data.dailyMinutes != null;
  /**
   * ★ 회사가 정한 이름·씀 여부를 얹는다 (2026-08-28 · 판 7).
   *   기준 설정에서 「경조」를 「경조휴가」로 고치면 여기가 바뀐다. 「안 씀」 으로 둔 종류는
   *   목록에서 빠진다 — **지난 신청에는 그대로 남는다**(그쪽은 제 이름을 따로 읽는다).
   */
  const codes = useCompanyCodes('attendance');
  useEffect(() => {
    if (tab !== 'leave') return;
    let alive = true;
    setPolicy(undefined);
    getWorkPolicy(startDate)
      .then((p) => { if (!alive) return; setPolicy(p); if (!isLegacyServer(p)) setNewServer(true); })
      // ★ 못 읽으면 옛 서버처럼 산다(종전 목록) — 새 서버로 이미 확인됐으면 시각 칸이 «불러오지 못함»을 말한다.
      .catch(() => { if (alive) setPolicy(null); });
    return () => { alive = false; };
  }, [tab, startDate]);

  /**
   * S6 W5 — 옛 서버 가드. 새 서버면 반차 · 반반차 하나씩(옛 오전/오후 넷은 뺀다), 옛 서버면 종전 목록 그대로.
   * ★ 새 코드를 옛 서버에 보내면 400 이다 — 고를 수 있는데 안 되는 것이 제일 나쁘다(아래 반반차 가드와 같은 원칙).
   */
  const leaveTypes = useMemo(
    () => codes.apply('leave_type', newServer
      ? LEAVE_TYPES.filter((t) => !isLegacyPartialType(t.key))
      : LEAVE_TYPES.filter((t) => !isPartialLeaveType(t.key) && (supportsQuarter || t.fraction !== 0.25))),
    [newServer, supportsQuarter, codes],
  );
  const tripTypes = useMemo(() => codes.apply('trip_kind', TRIP_TYPES), [codes]);
  /** v2 선택창의 묶음 · 잔여 · 막힘 — 깃발이 꺼져 있으면 쓰이지 않는다. */
  const leaveGroups = useMemo(() => buildLeaveTypeGroups(leaveTypes, summary.data), [leaveTypes, summary.data]);
  const isHalf = isPartialDay(leaveType);
  /** S6 — 시작 시각을 고르는 유형인가(새 서버 · 반차 · 반반차). 길이 · 범위 · 눈금은 그날 `partialLeave` 가 준다. */
  const partialKind = newServer && isPartialLeaveType(leaveType) ? leaveType : null;
  const partial = policy?.partialLeave ?? null;
  const timeWindow = partialKind && partial ? startWindow(partial, partialKind) : null;
  const endTime = partialKind && partial ? endTimeFor(partial, partialKind, startTime) : null;
  const timeOk = partialKind == null
    || (partial != null && partial.available && checkStart(partial, partialKind, startTime) === 'ok');
  const minutes = useMemo(
    () => (partialKind && partial
      ? partialLeaveMinutes(partial, partialKind)
      : countMinutes(leaveType, startDate, isHalf ? startDate : endDate, dailyMinutes)),
    [partialKind, partial, leaveType, startDate, endDate, isHalf, dailyMinutes],
  );

  // 날짜 · 유형이 바뀌어 고른 시각이 범위를 벗어나면 비운다 — 틀린 시각이 그대로 전송되지 않게.
  useEffect(() => {
    setTimeError(null);
    if (!partialKind || !partial || startTime == null) return;
    if (checkStart(partial, partialKind, startTime) !== 'ok') setStartTime(null);
  }, [partialKind, partial, startTime]);

  // 옛 서버로 확인됐는데 이미 반반차를 골라 뒀으면 연차로 되돌린다 — 목록에서 사라진 값이
  // 그대로 전송되면 400 이 된다.
  useEffect(() => {
    if (!supportsQuarter && (leaveType === 'quarter_am' || leaveType === 'quarter_pm')) setLeaveType('annual');
  }, [supportsQuarter, leaveType]);

  /**
   * ★ 회사가 「안 씀」으로 둔 종류를 골라 둔 채였다면 남은 첫 종류로 되돌린다 —
   *   목록에서 사라진 값이 그대로 전송되는 것을 막는다(위 반반차 되돌리기와 같은 규칙).
   */
  useEffect(() => {
    if (leaveTypes.length > 0 && !leaveTypes.some((t) => t.key === leaveType)) {
      setLeaveType(leaveTypes[0]!.key);
    }
  }, [leaveTypes, leaveType]);
  useEffect(() => {
    if (tripTypes.length > 0 && !tripTypes.some((t) => t.key === tripType)) {
      setTripType(tripTypes[0]!.key);
    }
  }, [tripTypes, tripType]);

  /**
   * 서버로 보낼 결재선 — **고른 것이 있을 때만** 싣는다.
   *
   * ★ 옛 서버는 이 칸을 모르고 `additionalProperties:false` 라 400 이 난다(배포 순서 결합 제거).
   * ★ 안 실으면 서버가 지금까지처럼 조직에서 자동으로 뽑는다 — 아무것도 안 바뀐다.
   */
  // v2 V9 — 출장 탭은 제 결재선을 싣는다(깃발 끔이면 `line` 그대로 · 종전과 같다).
  const activeLine = DASHBOARD_V2_ENABLED && tab === 'trip' ? tripLine : line;
  const lineBody = {
    ...(activeLine.approvers.length > 0 ? { approverIds: activeLine.approvers.map((a) => a.userId) } : {}),
    ...(activeLine.referrers.length > 0 ? { referrerIds: activeLine.referrers.map((r) => r.userId) } : {}),
  };

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = tab === 'leave'
        ? await createLeaveWithApproval({
            type: leaveType, startDate, endDate: isHalf ? startDate : endDate,
            reason: reason.trim() || '개인 사유', ...lineBody,
            // S6 — 새 코드일 때만 싣는다(끝 시각은 서버가 계산한다).
            ...(partialKind && startTime ? { startTime } : {}),
          })
        : await createBusinessTripWithApproval({
            type: tripType, destination: destination.trim(), startDate, endDate,
            purpose: purpose.trim() || '업무', ...lineBody,
          });
      invalidateWidgetCache('hr:*');

      // ★ 결재자를 못 찾았으면 **닫지 않는다** — 신청은 저장됐지만 아무도 승인할 수 없다는 사실을
      //   그 자리에서 말해야 한다(닫아 버리면 사용자는 올라간 줄 안다).
      if (res.approval?.blocked) {
        setApproval(res.approval);
        return;
      }
      onClose();
    } catch (err) {
      // S6 — 시각 거절(범위 밖 · 겹침 · 근무제)은 시작 칸 아래에 «무엇과 · 어디까지»를 적는다. 그 밖은 종전 알림.
      const body = tab === 'leave' && partialKind && axios.isAxiosError(err)
        ? (err.response?.data as LeaveTimeErrorBody | undefined)
        : undefined;
      const timeText = leaveTimeErrorText(body?.conflict
        ? { ...body, conflict: { ...body.conflict, typeLabel: body.conflict.typeLabel ?? leaveTypeLabel(body.conflict.type) } }
        : body);
      if (timeText) setTimeError(timeText);
      else setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = tab === 'leave'
    ? minutes > 0
      // v2: 잔여가 없어 막힌 유형이 골라져 있으면 끈다 — 고를 수 없는 것을 보낼 수는 없다(Q-11).
      && !(DASHBOARD_V2_ENABLED && isLeaveTypeBlocked(leaveGroups, leaveType))
      // S6 — 반차 · 반반차는 그날 쓸 수 있고 시작 시각이 범위 안일 때만.
      && timeOk
    : destination.trim() !== '';

  return (
    <Modal
      title="근태 신청"
      size="sm"
      onClose={onClose}
      footer={
        tab === 'punch'
          ? <ModalFootNote>출퇴근 기록은 근태에 남습니다</ModalFootNote>
          : (
            <>
              <ModalFootNote>
                {tab === 'leave'
                  ? <>신청 <b>{formatLeaveAmount(minutes, dailyMinutes)}</b>{startTime && endTime ? <> · <b>{startTime}–{endTime}</b></> : null}</>
                  : <>기간 <b>{countDays(startDate, endDate)}</b>일</>}
                {' · 신청하면 결재로 올라갑니다'}
              </ModalFootNote>
              <ModalActions>
                <button type="button" className="dw-btn" onClick={onClose}>취소</button>
                <button type="button" className="dw-btn dw-btn--primary" onClick={() => { void submit(); }} disabled={!canSubmit || submitting}>
                  {submitting ? '신청 중…' : '신청'}
                </button>
              </ModalActions>
            </>
          )
      }
    >
      <div className="dw-segbar">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            type="button"
            className={`dw-segbar__i${tab === key ? ' is-active' : ''}`}
            onClick={() => { setTab(key); setError(null); }}
          >
            <Icon size={13} aria-hidden />{label}
          </button>
        ))}
      </div>

      {error && <div className="dw-alert" role="alert">{error}</div>}
      {approval?.blocked && (
        <div className="dw-alert" role="alert">
          신청은 저장했지만 결재로 올리지 못했습니다 — {approval.reason ?? '조직에서 결재자를 찾지 못했습니다.'}
          {' '}관리자가 대표·부서장을 지정하면 그때 결재를 올릴 수 있습니다.
        </div>
      )}

      {tab === 'punch' && (
        <>
          <div className="dw-clock">
            <div className="dw-clock__time">{formatClock(tick)}</div>
            <div className="dw-clock__date">{new Date().toLocaleDateString(getDateLocale(), { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' })}</div>
          </div>
          <div className="dw-punch">
            <div className="dw-punch__cell">
              <div className="dw-punch__k">출근</div>
              <div className="dw-punch__v">{rec.checkIn ?? '--:--'}</div>
            </div>
            <div className="dw-punch__cell">
              <div className="dw-punch__k">퇴근</div>
              <div className={`dw-punch__v${rec.checkOut ? '' : ' dw-punch__v--empty'}`}>{rec.checkOut ?? '--:--'}</div>
            </div>
          </div>
          <div className="dw-punch__actions">
            <button
              type="button"
              className="dw-btn"
              onClick={() => { void selectStatus('work'); }}
              disabled={Boolean(rec.checkIn) || punchPending !== null}
            >
              {rec.checkIn
                ? <><Check size={14} aria-hidden />출근 완료</>
                : <><Clock size={14} aria-hidden />{punchPending === 'work' ? '기록 중…' : '출근하기'}</>}
            </button>
            {/* 퇴근은 출근한 사람만 — 위젯(AttendanceWidgets)과 같은 규칙(2026-08-23). */}
            <button
              type="button"
              className="dw-btn dw-btn--primary"
              onClick={() => { void selectStatus('off'); }}
              disabled={Boolean(rec.checkOut) || offLocked || punchPending !== null}
              title={offLocked ? '출근해야 열립니다' : undefined}
            >
              <LogOut size={14} aria-hidden />
              {rec.checkOut ? '퇴근 완료' : punchPending === 'off' ? '기록 중…' : '퇴근하기'}
            </button>
          </div>
          {punchError && <div className="dw-alert" role="alert">{punchError}</div>}
        </>
      )}

      {tab === 'leave' && (
        <>
          {DASHBOARD_V2_ENABLED ? (
            <LeaveTypeField
              groups={leaveGroups}
              value={leaveType}
              onChange={(v) => setLeaveType(v as LeaveType)}
              summary={summary.data}
              disabled={submitting}
            />
          ) : (
          <>
          <div className="dw-quota">
            <div className="dw-quota__main">
              <div className="dw-quota__k">잔여 연차</div>
              <div className="dw-quota__v">
                {/* 잔여는 **분에서 나온 문구** — 반반차를 쓰면 "14일 6시간" 처럼 남는다.
                    옛 서버(분 없음)면 예전처럼 일수만 보인다. */}
                {leaveTotalText(
                  leaveAvailable(summary.data?.annual) ?? 0,
                  leaveAvailable(summary.data?.annualMinutes),
                  dailyMinutes,
                )} <small>/ {summary.data?.annual.total ?? 0}일</small>
              </div>
            </div>
            <div
              className="dw-ring"
              style={{ '--dw-ring-pct': `${Math.round(((leaveAvailable(summary.data?.annual) ?? 0) / Math.max(1, summary.data?.annual.total ?? 1)) * 100)}%` } as React.CSSProperties}
              aria-hidden
            />
          </div>

          <div className="dw-field">
            <span className="dw-field__label">휴가 유형</span>
            <div className="dw-pills">
              {leaveTypes.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  className={`dw-pill${leaveType === t.key ? ' is-active' : ''}`}
                  onClick={() => setLeaveType(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          </>
          )}

          <div className="dw-grid2">
            <div className="dw-field">
              <label className="dw-field__label" htmlFor="dw-leave-start">시작일</label>
              <input id="dw-leave-start" type="date" className="dw-input" value={startDate}
                onChange={(e) => { setStartDate(e.target.value); if (e.target.value > endDate) setEndDate(e.target.value); }} />
            </div>
            <div className="dw-field">
              <label className="dw-field__label" htmlFor="dw-leave-end">종료일</label>
              <input id="dw-leave-end" type="date" className="dw-input" value={isHalf ? startDate : endDate}
                min={startDate} disabled={isHalf} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>

          {/* S6 — 반차 · 반반차 시작 시각(C4 TimePicker) · 끝 시각은 읽는 줄. 그날 못 쓰면 칸 대신 까닭. */}
          {partialKind && (
            policy === undefined ? (
              <p className="dw-field__hint">그날 근무 시간을 불러오는 중…</p>
            ) : !partial ? (
              <div className="dw-ptime__blocked" role="note">그날 근무제를 읽지 못해 {leaveTypeLabel(partialKind)}를 신청할 수 없습니다. 잠시 뒤 다시 시도해 주세요.</div>
            ) : !partial.available ? (
              <div className="dw-ptime__blocked" role="note">{unavailableText(partial.reason)}</div>
            ) : !timeWindow ? (
              <div className="dw-ptime__blocked" role="note">
                그날 근무 시간({partial.earliestStart}–{partial.latestEnd})에는 {leaveTypeLabel(partialKind)}를 낼 수 있는 시각이 없습니다.
              </div>
            ) : (
              <div className="dw-grid2">
                <div className="dw-field">
                  <span className="dw-field__label">시작 시각</span>
                  <TimePicker
                    ariaLabel="시작 시각"
                    value={startTime}
                    onChange={(v) => { setStartTime(v); setTimeError(null); }}
                    minuteStep={partial.stepMinutes}
                    min={timeWindow.min}
                    max={timeWindow.max}
                    invalid={timeError != null}
                    disabled={submitting}
                  />
                  {timeError
                    ? <span className="dw-ptime__err" role="alert">{timeError}</span>
                    : <span className="dw-field__hint">근무 시간 {partial.earliestStart}–{partial.latestEnd} · 시작 {timeWindow.min}–{timeWindow.max}</span>}
                </div>
                <div className="dw-field">
                  <span className="dw-field__label">끝 시각</span>
                  <div className="dw-ptime__end" aria-live="polite">
                    {endTime ? <b>{endTime}</b> : <span className="dw-field__hint">시작 시각을 고르면 정해집니다</span>}
                    <span className="dw-ptime__tag">자동</span>
                    <small>시작 + {durationText(partialLeaveMinutes(partial, partialKind))}</small>
                  </div>
                </div>
              </div>
            )
          )}

          <div className="dw-field">
            <label className="dw-field__label" htmlFor="dw-leave-reason">사유</label>
            <textarea id="dw-leave-reason" className="dw-input dw-input--area" rows={2}
              value={reason} onChange={(e) => setReason(e.target.value)} placeholder="개인 사유" />
          </div>

          <div className="dw-approver">
            <span className="dw-field__label">신청자</span>
            <Avatar userId={user?.id ?? 0} name={user?.name ?? '사용자'} src={user?.avatarUrl} size="sm" />
            <span className="dw-approver__n">{user?.name ?? '사용자'}</span>
            <span className="dw-field__hint">회사가 정한 기본 결재선이 먼저 들어옵니다</span>
          </div>

          {/* 결재선 — 기안 화면과 **같은 조각**이다 (결재 A6). 종전에는 결재선을 보지도 못하고
              서버가 자동으로 뽑은 결과만 받았다. 휴가는 일수를 크기로 넘겨 전결도 미리 보인다. */}
          <ApprovalLineBlock
            docType="LEAVE"
            measure={minutes > 0 ? minutes / dailyMinutes : null}
            value={line}
            onChange={setLine}
            disabled={submitting}
            onError={setError}
            favoritesVariant={DASHBOARD_V2_ENABLED ? 'select' : 'inline'}
          />
        </>
      )}

      {tab === 'trip' && (
        <>
          <div className="dw-field">
            <span className="dw-field__label">유형</span>
            <div className="dw-pills">
              {tripTypes.map((t) => (
                <button key={t.key} type="button" className={`dw-pill${tripType === t.key ? ' is-active' : ''}`} onClick={() => setTripType(t.key)}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div className="dw-field">
            <label className="dw-field__label" htmlFor="dw-trip-dest">목적지</label>
            <input id="dw-trip-dest" className="dw-input" value={destination}
              onChange={(e) => setDestination(e.target.value)} placeholder="예) 부산 고객사" />
          </div>
          <div className="dw-grid2">
            <div className="dw-field">
              <label className="dw-field__label" htmlFor="dw-trip-start">시작일</label>
              <input id="dw-trip-start" type="date" className="dw-input" value={startDate}
                onChange={(e) => { setStartDate(e.target.value); if (e.target.value > endDate) setEndDate(e.target.value); }} />
            </div>
            <div className="dw-field">
              <label className="dw-field__label" htmlFor="dw-trip-end">종료일</label>
              <input id="dw-trip-end" type="date" className="dw-input" value={endDate} min={startDate}
                onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>
          <div className="dw-field">
            <label className="dw-field__label" htmlFor="dw-trip-purpose">목적</label>
            <textarea id="dw-trip-purpose" className="dw-input dw-input--area" rows={2}
              value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder="업무 목적" />
          </div>

          {/* 같은 블록 — 출장도 휴가와 사람 고르는 방법이 같다 (결재 A6). 종류가 다르면
              기본 단계 수·전결 기준이 다르므로 `docType` 만 바꿔 준다. */}
          <ApprovalLineBlock
            docType={tripType === 'outside_work' ? 'OUTSIDE_WORK' : 'BUSINESS_TRIP'}
            measure={countDays(startDate, endDate) || null}
            value={DASHBOARD_V2_ENABLED ? tripLine : line}
            onChange={DASHBOARD_V2_ENABLED ? setTripLine : setLine}
            disabled={submitting}
            onError={setError}
            favoritesVariant={DASHBOARD_V2_ENABLED ? 'select' : 'inline'}
          />
        </>
      )}
    </Modal>
  );
}
